import {
  BASE_PATHS,
  FIVE_MINUTES_IN_SECONDS,
  PROTECTED_PATHS,
  PROTECTED_PATHS_PATTERNS,
} from '@belong/common';
import type { GoogleLoginError } from '@belong/ui';
import type { FacebookFailureResponse } from './types';

// todo: we might want to move this to @belong/ui
export function isGoogleLoginError(error: unknown): error is GoogleLoginError {
  return (
    typeof error === 'string' && ['INIT_FAILED', 'GENERIC'].includes(error)
  );
}

export function isFacebookFailureResponse(
  response: unknown,
): response is FacebookFailureResponse {
  return (
    typeof response === 'object' && response !== null && 'status' in response
  );
}

/**
 * Extracts the user unique ID from the query params.
 * Some Customer.io campaigns send it so the frontend can authenticate users in "Set Password" state via the /users/login-status/${uniqueId} endpoint.
 */
export function getUserUniqueIdFromQuery(
  query: URLSearchParams,
): string | null {
  const encodedUserData = query.get('data') ?? '';
  const decodedUserData = Buffer.from(encodedUserData, 'base64').toString();
  const userDataQuery = new URLSearchParams(decodedUserData);

  return userDataQuery.get('uniqueId');
}

export function getSafeReturnPath(value: unknown): string {
  if (typeof value !== 'string' || !value.startsWith('/')) {
    return BASE_PATHS.ACCOUNTS;
  }

  try {
    const origin = 'https://belonghome.com';
    const url = new URL(value, origin);
    const decodedPath = decodeURIComponent(url.pathname);
    if (
      url.origin !== origin ||
      decodedPath.startsWith('//') ||
      decodedPath.startsWith('/\\')
    ) {
      return BASE_PATHS.ACCOUNTS;
    }

    return `${url.pathname}${url.search}${url.hash}`;
  } catch {
    return BASE_PATHS.ACCOUNTS;
  }
}

/**
 * Parse a JWT token and return its payload.
 */
export function parseJWT(token: string) {
  /**
   * Extract the payload given the JWT format: `header.payload.signature`.
   */
  // `!` keeps the previous unguarded read: a malformed token threw here
  // before too, and the caller is what handles that.
  const base64URLPayload = token.split('.')[1]!;

  /**
   * Replace the URL-safe base64 characters with the standard base64 characters.
   */
  const base64Payload = base64URLPayload.replace(/-/g, '+').replace(/_/g, '/');

  /**
   * Decode the base64 payload and parse it as a JSON object.
   */
  const jsonPayload = Buffer.from(base64Payload, 'base64').toString('utf-8');

  return JSON.parse(jsonPayload);
}

/**
 * Check if a JWT token is expired.
 */
export function isJWTExpired(token: string): boolean {
  const parsedToken = parseJWT(token);
  const currentTimestamp = Math.floor(Date.now() / 1000);
  const isTokenExpired = parsedToken.exp < currentTimestamp;

  return isTokenExpired;
}

/**
 * Check if a path is protected.
 * A path is considered protected if it is nested under any of the top-level paths defined in PROTECTED_PATHS
 * or matches any pattern in PROTECTED_PATHS_PATTERNS (as a regular expression).
 */
export function isProtectedPath(path: string): boolean {
  return (
    Object.values(PROTECTED_PATHS).some((protectedPath) =>
      path.startsWith(protectedPath),
    ) ||
    Object.values(PROTECTED_PATHS_PATTERNS).some((protectedPathPattern) =>
      new RegExp(protectedPathPattern).test(path),
    )
  );
}

export function isPhoneNumber(emailOrPhone: string): boolean {
  if (!emailOrPhone) {
    return false;
  }

  if (/[()]/.test(emailOrPhone)) {
    return true;
  }

  const onlyPhoneChars = /^[\d\s+.-]+$/.test(emailOrPhone);
  const digitCount = (emailOrPhone.match(/\d/g) ?? []).length;

  return onlyPhoneChars && digitCount >= 5;
}

export function getInitialCountdown({
  timer,
}: Record<'timer', string | undefined>): number {
  if (timer && Number(timer) > 0 && Number(timer) <= FIVE_MINUTES_IN_SECONDS) {
    return Number(timer);
  }

  return FIVE_MINUTES_IN_SECONDS;
}
